import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Save, Download, Send, Copy, Mail, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { TagPreview } from "../../lib/tagShapes";
import { PageTitle, StatusBadge, STATUS_OPTIONS, TextArea } from "./shared";
import { ManualOrderForm } from "./ManualOrderForm";

const PAYMENT_OPTIONS = [
  { value: "unpaid", label: "UNPAID" },
  { value: "paid", label: "PAID" },
];

const FULFILMENT_STEPS = [
  { value: "not_sent", label: "NOT SENT" },
  { value: "forwarded", label: "FORWARDED" },
  { value: "accepted", label: "ACCEPTED" },
  { value: "dispatched", label: "DISPATCHED" },
  { value: "completed", label: "COMPLETED" },
];

const EtcheryGateway = ({ order, onChanged }) => {
  const [email, setEmail] = useState(null);
  const status = order.fulfilment?.status || "not_sent";

  const generate = async () => {
    const { data } = await api.get(`/admin/orders/${order.id}/etchery-email`);
    setEmail(data);
  };

  const setStatus = async (s) => {
    try {
      await api.patch(`/admin/orders/${order.id}/fulfilment`, { status: s });
      onChanged({ ...order, fulfilment: { ...(order.fulfilment || {}), status: s } });
      toast.success(`Fulfilment marked ${s.replace("_", " ").toUpperCase()}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const fullText = email ? `Subject: ${email.subject}\n\n${email.body}` : "";

  const copyEmail = async () => {
    await navigator.clipboard.writeText(fullText);
    toast.success("Fulfilment email copied to clipboard");
  };

  const downloadEmail = () => {
    const url = URL.createObjectURL(new Blob([fullText], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `etchery-fulfilment-${order.order_number}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-lilac-soft/40 border-2 border-lilac rounded-2xl p-4 space-y-3" data-testid={`etchery-gateway-${order.order_number}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-plum font-bold flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> ETCHERY FULFILMENT GATEWAY
        </p>
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {FULFILMENT_STEPS.map((s) => (
            <button key={s.value} onClick={() => setStatus(s.value)} data-testid={`fulfilment-${s.value}-${order.order_number}`}
              className={`px-3 py-1 rounded-full text-[9px] uppercase font-bold tracking-[0.08em] transition-colors ${status === s.value ? "bg-plum text-cream" : "bg-cream text-plum-deep/60 hover:text-plum"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {!email ? (
        <button onClick={generate} data-testid={`generate-etchery-email-${order.order_number}`}
          className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta">
          <Mail className="w-3.5 h-3.5" /> GENERATE FULFILMENT EMAIL
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-bold text-plum-deep">{email.subject}</p>
          <textarea readOnly value={email.body} rows={10} data-testid={`etchery-email-body-${order.order_number}`}
            className="w-full rounded-xl px-4 py-3 bg-cream border border-plum-deep/15 text-[12px] font-mono focus:outline-none" />
          <div className="flex flex-wrap gap-2">
            <button onClick={copyEmail} data-testid={`copy-etchery-email-${order.order_number}`} className="inline-flex items-center gap-1.5 rounded-full bg-magenta text-cream px-4 py-2 text-[11px] uppercase font-bold tracking-[0.1em] hover:bg-plum"><Copy className="w-3 h-3" /> COPY</button>
            <a href={`mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`} data-testid={`mailto-etchery-${order.order_number}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-plum-deep/20 text-plum-deep px-4 py-2 text-[11px] uppercase font-bold tracking-[0.1em] hover:border-magenta"><Mail className="w-3 h-3" /> OPEN IN EMAIL</a>
            <button onClick={downloadEmail} data-testid={`download-etchery-email-${order.order_number}`} className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-plum-deep/20 text-plum-deep px-4 py-2 text-[11px] uppercase font-bold tracking-[0.1em] hover:border-magenta"><Download className="w-3 h-3" /> DOWNLOAD .TXT</button>
            {status === "not_sent" && (
              <button onClick={() => setStatus("forwarded")} data-testid={`mark-forwarded-${order.order_number}`} className="inline-flex items-center gap-1.5 rounded-full bg-forest text-cream px-4 py-2 text-[11px] uppercase font-bold tracking-[0.1em] hover:bg-plum"><Send className="w-3 h-3" /> MARK AS FORWARDED</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const OrderRow = ({ order, onChanged, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(order.admin_notes || "");

  const patch = async (body, msg) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, body);
      toast.success(msg);
      onChanged({ ...order, ...body });
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete order ${order.order_number}?`)) return;
    await api.delete(`/admin/orders/${order.id}`);
    toast.success("Order deleted");
    onDeleted(order.id);
  };

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] overflow-hidden" data-testid={`order-row-${order.order_number}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-5 py-4 text-left" data-testid={`order-toggle-${order.order_number}`}>
        <span className="font-semibold text-sm w-20 shrink-0">{order.order_number}</span>
        {order.source === "manual" && <span className="hidden sm:inline-block text-[9px] px-2 py-0.5 rounded-full bg-plum/15 text-plum uppercase font-bold shrink-0">MANUAL</span>}
        {order.order_type === "supply" && <span className="inline-block text-[9px] px-2 py-0.5 rounded-full bg-sky/25 text-sky-deep uppercase font-bold shrink-0" data-testid={`order-supply-badge-${order.order_number}`}>SUPPLY</span>}
        <span className="flex-1 text-sm truncate">{order.customer?.name} · {order.customer?.email}</span>
        <span className="hidden sm:block text-xs text-plum-deep/60">{new Date(order.created_at).toLocaleDateString("en-NZ")}</span>
        <span className="text-sm font-semibold">${order.total?.toFixed(2)}</span>
        <StatusBadge status={order.status} />
        <span className={`px-2 py-1 rounded-full text-[10px] uppercase font-bold ${order.payment_status === "paid" ? "bg-forest/15 text-forest" : "bg-lemon/50 text-plum-deep"}`}>
          {order.payment_status}
        </span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-plum-deep/10 pt-4 space-y-5" data-testid={`order-detail-${order.order_number}`}>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">ITEMS</p>
              <ul className="space-y-2">
                {order.items?.map((it, i) => (
                  <li key={i} className="flex items-center gap-3 bg-cream rounded-xl p-2.5">
                    {it.product_id ? (
                      <>
                        {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 border border-plum-deep/10" />}
                        <div className="flex-1 min-w-0 text-sm">
                          <p className="font-medium truncate">{it.title}</p>
                          <p className="text-xs text-plum-deep/60">{it.variant_title ? `${it.variant_title} · ` : ""}×{it.quantity}{it.supplier ? ` · ${it.supplier}` : ""}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <TagPreview shapeKey={it.shape_key} hex={it.colour_hex} line1={it.text_line1} line2={it.text_line2} size={it.size} icon={it.icon} className="w-9 h-12 shrink-0" />
                        <div className="flex-1 min-w-0 text-sm">
                          <p className="font-medium truncate">{it.bundle_name ? it.bundle_name : `"${it.text_line1}"${it.text_line2 ? ` / ${it.text_line2}` : ""}`}</p>
                          <p className="text-xs text-plum-deep/60">{it.bundle_name ? `${it.texts?.length || 0} tags · ` : ""}{it.shape_name}{it.size ? ` ${it.size}` : ""} · {it.colour_name} · ×{it.quantity}{it.icon?.key ? ` · ICON: ${it.icon.key.toUpperCase()}` : ""}</p>
                          {it.bundle_name && it.texts?.length > 0 && <p className="text-[11px] text-plum-deep/50 truncate">{it.texts.join(" · ")}</p>}
                        </div>
                      </>
                    )}
                    <span className="text-sm">${it.line_total?.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {order.notes && <p className="mt-3 text-xs text-plum-deep/70 bg-lemon/20 rounded-xl p-3">Customer note: {order.notes}</p>}
              <div className="mt-3 text-xs text-plum-deep/60 space-y-0.5">
                {order.subtotal != null && <p>Subtotal: ${order.subtotal.toFixed(2)}{order.discount > 0 ? ` · Discount (${order.discount_percent}%): −$${order.discount.toFixed(2)}` : ""}{order.voucher_amount > 0 ? ` · Voucher ${order.voucher_code}: −$${order.voucher_amount.toFixed(2)}` : ""}{order.voucher_code && !(order.voucher_amount > 0) ? ` · Voucher ${order.voucher_code}` : ""} · Shipping: {order.shipping ? `$${order.shipping.toFixed(2)}` : "FREE"}</p>}
                {order.tracking_number && <p data-testid={`order-tracking-${order.order_number}`}>Tracking: {order.tracking_number}</p>}
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">CUSTOMER</p>
                <p className="text-sm">{order.customer?.name}</p>
                <p className="text-sm text-plum-deep/70">{order.customer?.email}</p>
                {order.customer?.phone && <p className="text-sm text-plum-deep/70">{order.customer.phone}</p>}
                {order.customer?.address && <p className="text-sm text-plum-deep/70">{order.customer.address}</p>}
              </div>
              <div className="flex flex-wrap gap-3">
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">STATUS</span>
                  <select
                    value={order.status}
                    onChange={(e) => patch({ status: e.target.value }, "Status updated")}
                    data-testid={`order-status-select-${order.order_number}`}
                    className="rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">PAYMENT</span>
                  <select
                    value={order.payment_status}
                    onChange={(e) => patch({ payment_status: e.target.value }, "Payment updated")}
                    data-testid={`order-payment-select-${order.order_number}`}
                    className="rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta"
                  >
                    {PAYMENT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
              </div>
              <div>
                <span className="block text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">ADMIN NOTES</span>
                <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`order-admin-notes-${order.order_number}`} />
                <div className="flex gap-3 mt-2">
                  <button onClick={() => patch({ admin_notes: notes }, "Notes saved")} className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-plum" data-testid={`order-save-notes-${order.order_number}`}>
                    <Save className="w-3.5 h-3.5" /> SAVE NOTES
                  </button>
                  <button onClick={remove} className="inline-flex items-center gap-2 rounded-full bg-cream border border-plum-deep/20 text-plum-deep px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:text-red-600" data-testid={`order-delete-${order.order_number}`}>
                    <Trash2 className="w-3.5 h-3.5" /> DELETE
                  </button>
                </div>
              </div>
            </div>
          </div>
          {order.order_type === "supply" && <EtcheryGateway order={order} onChanged={onChanged} />}
        </div>
      )}
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [srcFilter, setSrcFilter] = useState("all");
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  const filtered = (orders || []).filter((o) =>
    (filter === "all" || o.status === filter) &&
    (srcFilter === "all" || (srcFilter === "supply" ? o.order_type === "supply" : o.order_type !== "supply"))
  );

  const exportCsv = async () => {
    const res = await api.get("/admin/orders-export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bloom-foundry-orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="console-orders">
      <PageTitle kicker="UNIFIED CRM · ETCHERY + SUPPLY ROOM" title="ORDERS">
        <div className="flex gap-3">
          <button
            onClick={() => setShowManual((v) => !v)}
            data-testid="orders-new-manual"
            className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta"
          >
            <Plus className="w-3.5 h-3.5" /> MANUAL ORDER / QUOTE
          </button>
          <button
            onClick={exportCsv}
            data-testid="orders-export-csv"
            className="inline-flex items-center gap-2 rounded-full bg-warmwhite border border-plum-deep/15 text-plum-deep px-5 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:border-magenta"
          >
            <Download className="w-3.5 h-3.5" /> EXPORT CSV
          </button>
        </div>
      </PageTitle>

      {showManual && (
        <ManualOrderForm
          onClose={() => setShowManual(false)}
          onCreated={(order) => { setOrders((l) => [order, ...(l || [])]); setShowManual(false); }}
        />
      )}
      <div className="flex flex-wrap gap-2 mb-3">
        {[{ value: "all", label: "ALL SOURCES" }, { value: "workshop", label: "WORKSHOP & TAGS" }, { value: "supply", label: "SUPPLY ROOM" }].map((s) => (
          <button
            key={s.value}
            onClick={() => setSrcFilter(s.value)}
            data-testid={`orders-source-${s.value}`}
            className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold transition-colors ${srcFilter === s.value ? "bg-magenta text-cream" : "bg-warmwhite border border-plum-deep/15 text-plum-deep hover:border-magenta"}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {[{ value: "all", label: "ALL" }, ...STATUS_OPTIONS].map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            data-testid={`orders-filter-${s.value}`}
            className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-semibold transition-colors ${filter === s.value ? "bg-plum text-cream" : "bg-warmwhite border border-plum-deep/15 text-plum-deep hover:border-magenta"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {orders === null ? (
        <p className="text-plum-deep/60">Loading orders…</p>
      ) : filtered.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="orders-empty">No orders here yet.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <OrderRow
              key={o.id}
              order={o}
              onChanged={(updated) => setOrders((list) => list.map((x) => (x.id === updated.id ? updated : x)))}
              onDeleted={(id) => setOrders((list) => list.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
